const TAU = Math.PI * 2
const DEFAULT_MAX_SAGITTA = 0.002
const MIN_SEGMENTS = 32
const MAX_SEGMENTS = 1024

/** 圆弧几何计算工具，提供段数估计与扫角归一化等静态方法 */
export class ArcUtils {
  /**
   * 根据圆的半径和屏幕像素距离动态计算圆弧段数。
   *
   * @param radius - 圆半径，单位世界坐标
   * @param worldDistancePerPixel - 每像素对应的世界单位
   * @returns 动态计算的圆弧段数，夹在 [MIN_SEGMENTS, MAX_SEGMENTS] 之间
   */
  static getDynamicSegments(radius: number, worldDistancePerPixel: number): number {
    const visualRadius = radius / worldDistancePerPixel
    const segments = Math.floor((TAU * visualRadius) / 5)
    return Math.min(MAX_SEGMENTS, Math.max(MIN_SEGMENTS, segments))
  }

  /**
   * 计算从 startAngle 到 endAngle 的扫过角度，结果始终为正值。
   *
   * @param startAngle - 起始角度，单位弧度
   * @param endAngle - 终止角度，单位弧度
   * @param clockwise - 是否顺时针扫描，默认 false
   * @returns 归一化扫角，范围 (0, 2π]；整圆返回 2π
   */
  static getSweepAngle(startAngle: number, endAngle: number, clockwise: boolean = false): number {
    const rawSweep = clockwise ? startAngle - endAngle : endAngle - startAngle
    let normalizedSweep = rawSweep % TAU

    if (normalizedSweep < 0) {
      normalizedSweep += TAU
    }

    // 整圆检测：归一化后接近 0 但原始值不为 0，说明是整圆
    if (normalizedSweep < Number.EPSILON && Math.abs(rawSweep) > Number.EPSILON) {
      return TAU
    }

    return normalizedSweep
  }

  /**
   * 基于弓高误差（sagitta）计算圆弧需要的分段数。
   *
   * @param radius - 圆半径，单位世界坐标，需 > 0
   * @param sweepAngle - 扫过角度，单位弧度
   * @param maxSagitta - 允许的最大弓高误差，默认 {@link DEFAULT_MAX_SAGITTA}
   * @returns 分段数，夹在 [MIN_SEGMENTS, MAX_SEGMENTS] 之间
   */
  static getStaticSegments(
    radius: number,
    sweepAngle: number,
    maxSagitta: number = DEFAULT_MAX_SAGITTA
  ): number {
    if (!Number.isFinite(radius) || radius <= 0) {
      return MIN_SEGMENTS
    }

    const normalizedSweep = ArcUtils.normalizeSweepAngle(sweepAngle)
    if (normalizedSweep <= 0) {
      return MIN_SEGMENTS
    }

    // 限制 sagitta 不超过半径，防止 acos 参数越界
    const effectiveSagitta = Math.min(Math.abs(maxSagitta), radius)
    if (effectiveSagitta <= 0) {
      return MIN_SEGMENTS
    }

    const cosValue = 1 - effectiveSagitta / radius
    const maxSegmentAngle = 2 * Math.acos(Math.min(1, Math.max(-1, cosValue)))

    if (!Number.isFinite(maxSegmentAngle) || maxSegmentAngle <= 0) {
      return MAX_SEGMENTS
    }

    const segments = Math.ceil(normalizedSweep / maxSegmentAngle)
    return Math.min(MAX_SEGMENTS, Math.max(MIN_SEGMENTS, segments))
  }

  /** 将扫过角度归一化到 (0, 2π] */
  private static normalizeSweepAngle(sweepAngle: number): number {
    if (!Number.isFinite(sweepAngle)) {
      return 0
    }

    const normalizedSweep = Math.abs(sweepAngle) % TAU
    // 整圆检测
    if (normalizedSweep < Number.EPSILON && Math.abs(sweepAngle) > Number.EPSILON) {
      return TAU
    }

    return normalizedSweep
  }
}
